import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/primitives/dialog";
import { Button } from "@/components/primitives/button";
import { safeStorage } from "@/utils/safeStorage";
import { useAnnouncementSlot } from "@/zustand/AnnouncementState";
import { EMERALD_NOTE_SEEN_KEY } from "@/lib/emeraldNote";

export { EMERALD_NOTE_SEEN_KEY, suppressEmeraldNote } from "@/lib/emeraldNote";

/**
 * One-time announcement from Robert shown the first time an existing user
 * opens the app on the Midnight Emerald release. Dismissing it (button or
 * closing the dialog) persists — it never shows again.
 *
 * The copy is Robert's verbatim note and intentionally not translated.
 */
export default function EmeraldNoteDialog() {
  const canShow = useAnnouncementSlot("emerald-note");
  const [open, setOpen] = useState(
    () => safeStorage.get(EMERALD_NOTE_SEEN_KEY) !== "true",
  );

  const dismiss = (nextOpen: boolean) => {
    if (!nextOpen) {
      safeStorage.set(EMERALD_NOTE_SEEN_KEY, "true");
      setOpen(false);
    }
  };

  if (!canShow || !open) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={dismiss}>
      {/* Pinned header (title + X) and footer (Continue); only the letter
          body scrolls. Max height clears the iOS safe areas so neither end
          ever hides under the clock or the home indicator. */}
      <DialogContent
        closeClassName="top-[max(1rem,env(safe-area-inset-top,0px))]"
        className="flex max-h-[calc(100dvh-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px)-2rem)] max-w-lg flex-col gap-0 border-primary/40 p-0"
      >
        <DialogHeader className="relative shrink-0 px-6 pb-3 pt-[max(1.5rem,env(safe-area-inset-top,0px))]">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(circle_at_top,_hsl(var(--primary)/0.35),_transparent_70%)]" />
          <DialogTitle className="text-center text-xl">
            A Note from Robert
          </DialogTitle>
        </DialogHeader>

        <DialogDescription asChild>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-2 text-sm leading-relaxed text-foreground/90">
            <p>You may notice Architect looks a little different today.</p>
            <p>
              We&rsquo;ve introduced a new default theme called{" "}
              <span className="font-medium text-secondary">
                Midnight Emerald
              </span>
              .
            </p>
            <p>This wasn&rsquo;t simply a design decision.</p>
            <p>
              As Architect has evolved with the full integration of Polymath
              BFN, we&rsquo;ve been thinking a great deal about the future of
              artificial intelligence&mdash;and the responsibility that comes
              with building it.
            </p>
            <p>
              The AI industry is moving toward ever-larger models that require
              extraordinary amounts of computation and energy. We believe
              intelligence doesn&rsquo;t have to come at that cost.
            </p>
            <p>Architect was built differently.</p>
            <p>
              By combining highly optimized Small Language Models with our
              Polymath BFN cognitive framework, we&rsquo;ve dramatically reduced
              the amount of computation required while continuing to expand
              Architect&rsquo;s capabilities.
            </p>
            <p>Midnight Emerald reflects that philosophy.</p>
            <p>
              It&rsquo;s inspired by the Emerald Tablets&mdash;a timeless symbol
              of knowledge and discovery&mdash;but it also represents something
              equally important: our commitment to building AI that is more
              sustainable, more efficient, and ultimately more accessible to
              everyone.
            </p>
            <p>
              This is just one small visual change, but it represents a much
              bigger idea.
            </p>
            <p>Thank you for being part of this journey.</p>
            <p className="pt-1 font-medium text-foreground">
              &mdash; Robert Grant
            </p>
          </div>
        </DialogDescription>

        <div className="shrink-0 border-t border-border/60 px-6 py-4">
          <Button className="w-full" onClick={() => dismiss(false)}>
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
